import { useCallback, useEffect, useState } from "react";
import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";
import { useIsNewOnboardingFlow } from "../hooks/useIsNewOnboardingFlow";

interface OnboardingTrackerProps {
  config?: {
    trackStartedAutomatically?: boolean;
  };
  fields: {
    name: string;
    type: "text" | "select";
    options?: string[];
    value?: string;
  }[];
  source?: string;
  step: string;
  step_number: number;
  total_steps: number;
}

export const useOnboardingTracker = ({
  step_number,
  total_steps,
  step,
  fields,
  source,
  config = {
    trackStartedAutomatically: true,
  },
}: OnboardingTrackerProps) => {
  const { trackEvent } = useEventsTracker();
  const [startedAt] = useState<Date>(new Date());
  const [, setIsStartedEventTracked] = useState(false);
  const [, setIsCompletedEventTracked] = useState(false);
  const [isCommunitySetupEventTracked, setIsCommunitySetupEventTracked] =
    useState(false);
  const { isNewOnboardingFlow } = useIsNewOnboardingFlow();

  useEffect(() => {
    if (config.trackStartedAutomatically) {
      trackStepStarted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want to track the step started only once
  }, []);

  const trackStepStarted = useCallback(() => {
    setIsStartedEventTracked(isStartedEventTracked => {
      if (isStartedEventTracked) {
        return isStartedEventTracked;
      }

      trackEvent("onboarding.step.started", {
        step_number,
        total_steps,
        step,
        started_at: startedAt,
        fields: fields,
        isNewOnboardingFlow,
        ...(source && { source }),
      });
      return true;
    });
  }, [
    setIsStartedEventTracked,
    startedAt,
    step_number,
    step,
    total_steps,
    trackEvent,
    fields,
    isNewOnboardingFlow,
    source,
  ]);

  const trackStepCompleted = useCallback(
    (values: string[]) => {
      setIsCompletedEventTracked(isCompletedEventTracked => {
        if (isCompletedEventTracked) {
          return isCompletedEventTracked;
        }

        trackEvent("onboarding.step.completed", {
          step_number,
          total_steps,
          step,
          started_at: startedAt,
          completed_at: new Date(),
          fields: fields.map((field, index) => ({
            ...field,
            value: values[index],
          })),
          isNewOnboardingFlow,
          ...(source && { source }),
        });
        return true;
      });
    },
    [
      setIsCompletedEventTracked,
      startedAt,
      step_number,
      step,
      total_steps,
      trackEvent,
      fields,
      isNewOnboardingFlow,
      source,
    ],
  );

  const trackCommunitySetupCompleted = useCallback(
    ({ isBusiness }: { isBusiness: boolean }) => {
      if (isCommunitySetupEventTracked) {
        return;
      }

      trackEvent("community.setup.completed", {
        isBusiness,
        isNewOnboardingFlow,
        ...(source && { source }),
      });
      setIsCommunitySetupEventTracked(true);
    },
    [isCommunitySetupEventTracked, trackEvent, isNewOnboardingFlow, source],
  );

  return {
    trackStepStarted,
    trackStepCompleted,
    trackCommunitySetupCompleted,
  };
};
